import { FC, FormEvent, useCallback, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  Stack,
  CircularProgress,
  Typography,
} from '@mui/material';
import LoginFormPasswordField from '../../../Login/LoginFormPasswordField';
import { useYupValidationResolver } from '@vs-online-only-app/ui';
import {
  updatePasswordByTokenRequest,
  UpdatePasswordFormFieldValues,
} from '@vs-online-only-app/state';
import { AxiosError } from 'axios';
import { createPasswordValidationSchema } from '../../create-account/steps/password/EnterPassword';
import ResetPasswordBackButton from '../ResetPasswordBackButton';

type Props = {
  email: string;
  token: string;
  nextStep: (v: string) => void;
  previousStep: () => void;
};

const ResetPasswordStep3: FC<Props> = ({
  email,
  token,
  nextStep,
  previousStep,
}) => {
  const resolver = useYupValidationResolver(createPasswordValidationSchema);

  const passwordRef = useRef<HTMLInputElement>(null);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  const [isSubmitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const formMethods = useForm<UpdatePasswordFormFieldValues>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    resolver,
  });

  const { errors } = formMethods.formState;

  const handleFieldBlur = async (
    fieldName: keyof UpdatePasswordFormFieldValues,
  ) => {
    formMethods.trigger(fieldName);
  };

  const handleFieldChange = useCallback(
    (params: { field: keyof UpdatePasswordFormFieldValues; value: string }) => {
      if (
        (params.field === 'password' && params.value.length) ||
        (params.field === 'confirmPassword' && params.value.length)
      ) {
        setErrorMessage('');
        setSubmitButtonDisabled(false);
      }
      formMethods.setValue(params.field, params.value);
    },
    [formMethods],
  );

  const doCreatePasswordRequest = useCallback(
    async (password: string, passwordConfirm: string) => {
      setIsLoading(true);
      try {
        await updatePasswordByTokenRequest({
          email,
          password,
          passwordConfirm,
          token,
        });
        nextStep(password);
        setIsLoading(false);
      } catch (e) {
        setSubmitButtonDisabled(true);
        setIsLoading(false);
        if (
          e instanceof AxiosError &&
          // TODO: Import error codes from Backend to match here? ([hero-backend]/api/error/errors.ts)
          [4316, 4315].includes(e.response?.data.code)
        ) {
          setErrorMessage(
            e.response?.data.message, // TODO: Change copy? This might not be localised?
          );
        } else {
          setErrorMessage('Could not create new password');
        }
      }
    },
    [token, email, nextStep],
  );

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    formMethods.handleSubmit(async (formValues) => {
      const { password, confirmPassword } = formValues;
      if (password !== confirmPassword) {
        setErrorMessage('Passwords do not match');
        setSubmitButtonDisabled(true);
        return;
      }

      doCreatePasswordRequest(password, confirmPassword);
    })();
  };

  const triggerFormSubmit = useCallback(async () => {
    if (
      !passwordRef.current ||
      !passwordRef.current.value ||
      !confirmPasswordRef.current ||
      !confirmPasswordRef.current.value
    )
      return;

    doCreatePasswordRequest(
      passwordRef.current.value,
      confirmPasswordRef.current.value,
    );
  }, [doCreatePasswordRequest, passwordRef, confirmPasswordRef]);

  return (
    <Stack gap={3}>
      <ResetPasswordBackButton goToPreviousStep={previousStep} />
      <Typography align="center" variant="h4">
        Create a Password
      </Typography>
      <Typography align="center">
        Your password should have at least 8 characters or more and at least one
        number or special character.
      </Typography>
      <FormProvider {...formMethods}>
        <form onSubmit={handleFormSubmit}>
          <Stack gap={2}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            <LoginFormPasswordField
              inputRef={passwordRef}
              error={!!errors.password}
              onBlur={() => handleFieldBlur('password')}
              onChange={(value) =>
                handleFieldChange({ field: 'password', value })
              }
            />
            <LoginFormPasswordField
              inputRef={confirmPasswordRef}
              error={!!errors.confirmPassword}
              onBlur={() => handleFieldBlur('confirmPassword')}
              onChange={(value) =>
                handleFieldChange({ field: 'confirmPassword', value })
              }
              label="Confirm password"
            />
          </Stack>
        </form>
      </FormProvider>
      <Button
        disabled={isSubmitButtonDisabled || isLoading}
        variant="contained"
        onClick={triggerFormSubmit}
        size="large"
      >
        {isLoading ? (
          <CircularProgress color="inherit" size={20} />
        ) : (
          'Create Password'
        )}
      </Button>
    </Stack>
  );
};

export default ResetPasswordStep3;
